import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../../lib/api';
import { toast } from 'sonner';
import { LinkSimple, Copy, Code } from '@phosphor-icons/react';

export const LeadFormCard = () => {
  const qc = useQueryClient();
  const [msg, setMsg] = useState(null);
  const { data, error } = useQuery({ queryKey: ['lead-settings'], queryFn: () => api.get('/admissions/lead-settings').then((r) => r.data), retry: 0 });
  const toggleMut = useMutation({
    mutationFn: () => api.post('/admissions/lead-settings', { enabled: !data.enabled }),
    onSuccess: (r) => { toast.success(r.data.enabled ? 'Lead form is live' : 'Lead form paused'); qc.invalidateQueries({ queryKey: ['lead-settings'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const msgMut = useMutation({
    mutationFn: () => api.post('/admissions/lead-settings', { auto_reply_message: msg }),
    onSuccess: () => { toast.success('Auto-reply message saved'); setMsg(null); qc.invalidateQueries({ queryKey: ['lead-settings'] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (error || !data) return null;
  const url = `${window.location.origin}/apply/${data.code}`;
  const snippet = `<iframe src="${url}?embed=1" width="100%" height="680" style="border:0;border-radius:12px" title="Admission form"></iframe>`;
  const copy = (text, label) => { navigator.clipboard.writeText(text); toast.success(`${label} copied`); };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid="lead-form-card">
      <div className="flex items-center gap-2 mb-2">
        <LinkSimple size={15} className="text-orange-600" />
        <p className="font-mono-label text-[10px] uppercase text-slate-400">Public lead form</p>
        <button data-testid="lead-form-toggle" onClick={() => toggleMut.mutate()}
          className={`ms-auto font-mono-label text-[9px] uppercase px-2 py-0.5 rounded ${data.enabled ? 'bg-green-100 text-green-800' : 'bg-slate-100 text-slate-500'}`}>
          {data.enabled ? 'live' : 'paused'}
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-2">Share this link in ads and social posts, or embed the form on your school website. Add ?utm_campaign=summer2026 to track campaigns.</p>
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded p-2 mb-2">
        <span className="text-xs font-mono break-all flex-1" data-testid="lead-form-url">{url}</span>
        <button data-testid="lead-form-copy-url" onClick={() => copy(url, 'Link')} className="text-slate-400 hover:text-slate-900 shrink-0"><Copy size={14} /></button>
      </div>
      <button data-testid="lead-form-copy-embed" onClick={() => copy(snippet, 'Embed snippet')}
        className="text-xs border border-slate-200 rounded px-2 py-1 hover:border-orange-500 flex items-center gap-1.5">
        <Code size={13} /> Copy iframe embed snippet
      </button>
      <div className="mt-3 pt-3 border-t border-slate-100">
        <p className="font-mono-label text-[10px] uppercase text-slate-400 mb-1">Auto-reply to families</p>
        <p className="text-xs text-slate-500 mb-1.5">Every submission gets an instant branded confirmation email. Add an optional extra line (e.g. campus visits every Saturday 10am).</p>
        <textarea data-testid="lead-form-auto-reply" value={msg ?? data.auto_reply_message ?? ''} onChange={(e) => setMsg(e.target.value)}
          placeholder="Optional extra message appended to the confirmation email..."
          className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs h-14" />
        {msg !== null && msg !== (data.auto_reply_message || '') && (
          <button data-testid="lead-form-auto-reply-save" onClick={() => msgMut.mutate()} disabled={msgMut.isPending}
            className="text-xs bg-slate-900 text-white rounded px-2.5 py-1 hover:bg-slate-800 mt-1">Save message</button>
        )}
      </div>
    </div>
  );
};
